import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useCallback } from "react";
import { InfiniteData, useMutation, useQuery } from "@tanstack/react-query";
import {
  WorkOrderCount,
  RFIDSelectStationWidget,
  RFIDWidget,
  UpdateProductionJobModal,
  RFIDTagHistoryAndMoveModal,
  RFIDSearchBar,
  type RFIDSearchBarOptions,
} from "@dmca/frontend/widgets/rfid";
import {
  checkPermissionsInLoader,
  useGlobalContext,
  useLocalStorage,
  useAppPermissions,
  useSubscription,
} from "@dmca/frontend/hooks";
import {
  Box,
  Card,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Button } from "@dmca/frontend/components/button";
import Search from "@mui/icons-material/Search";
import SendToMobile from "@mui/icons-material/SendToMobile";
import { SelectionModal } from "@dmca/frontend/widgets/production";
import { IProductionJobWithLocation } from "@dmca/models/interface/production-job";
import { toast } from "react-toastify";
import { isEqual } from "lodash";
import { DateTime } from "@dmca/utils/luxon";
import type { inferOutput } from "@trpc/tanstack-react-query";
import { PageHeader } from "@dmca/frontend/components/nav";
import { CellFeaturesEnum } from "@dmca/models/interface/cell-location";
import superjson from "@dmca/utils/superjson";

export const Route = createFileRoute("/_authed/rfid")({
  component: RFIDOverview,
  loader: async ({ context }) => {
    await checkPermissionsInLoader("RFID::ViewRFIDOverview", context);
    const { qc, trpcQcProxy: trpc } = context;
    qc.prefetchQuery(
      trpc.cell.getAll.queryOptions({
        byFeatureFlag: CellFeaturesEnum.RFID,
      })
    );
    if (typeof window === "undefined") {
      return;
    }
    let selectedLocations: string[] = [];
    try {
      const selectedLocationsStr = window.localStorage.getItem(
        "rfidOverviewLocations"
      );
      if (!selectedLocationsStr) {
        return;
      }
      selectedLocations = superjson.parse(selectedLocationsStr);
    } catch (e) {
      console.error(
        "Error parsing rfidOverviewLocations from localStorage:",
        e
      );
      return;
    }

    if (selectedLocations.length === 0) {
      return;
    }

    const selectedLocationsInitial = await qc.ensureQueryData(
      trpc.productionJob.getAllWithLocationPartitioned.queryOptions({
        sort: "rfid",
        locationIds: selectedLocations.map((id) => ({
          id,
          page: 0,
          limit: 20,
        })),
      })
    );

    // updating the cache instead of passing the data in as initialData, since react query would then assume every query key has the same initial data and never invalidate
    Object.entries(selectedLocationsInitial).forEach(([cellId, data]) => {
      qc.setQueryData(
        trpc.productionJob.getAllWithLocationInfinite.infiniteQueryKey({
          limit: 20,
          locationId: cellId,
          searchTerm: "",
          showHighPriorityOnly: false,
          showProjectType: "both",
          sort: "rfid",
        }),
        {
          pages: [data],
          pageParams: [0],
        }
      );
    });
  },
  head: () => ({
    meta: [
      {
        title: "RFID Overview | DNA Dashboard",
      },
    ],
  }),
});

function RFIDOverview() {
  const { qc, trpcQcProxy: trpc } = Route.useRouteContext();
  const hasPermission = useAppPermissions();
  const [isDisplayMode, setIsDisplayMode] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [tagCount, setTagCount] = useState(0);
  const [searchValue, setSearchValue] = useState<RFIDSearchBarOptions>({
    search: "",
    showProject: "both",
    showHighPriorityOnly: false,
  });
  const [selectedJob, setSelectedJob] = useState<IProductionJobWithLocation>();
  const [currentModal, setCurrentModal] = useState<
    "UpdateWorkOrder" | "RFIDUtils"
  >();
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [locations, setLocations] = useLocalStorage<string[]>(
    "rfidOverviewLocations",
    []
  );

  const {
    navIsOpen: [, setNavIsOpen],
  } = useGlobalContext();

  useEffect(() => {
    if (isDisplayMode) {
      setNavIsOpen(false);
    }
  }, [isDisplayMode, setNavIsOpen]);

  useEffect(() => {
    if (!showSearchBar) {
      setSearchValue((old) => ({
        ...old,
        search: "",
      }));
    }
  }, [showSearchBar]);

  useEffect(() => {
    let cancel: NodeJS.Timeout | undefined = undefined;
    const u = qc.getQueryCache().subscribe((query) => {
      // Skip unnecessary processing for irrelevant query types
      if (
        !(
          query.type === "updated" ||
          query.type === "observerRemoved" ||
          query.type === "observerOptionsUpdated"
        )
      )
        return;

      if (
        !(
          query.query.state.status !== "pending" &&
          query.query.state.fetchStatus === "idle"
        )
      )
        return;

      // this is somewhat hardcoded right now, maybe improvement down the road?
      const key = query.query.queryKey;
      if (
        !isEqual(
          key[0],
          trpc.productionJob.getAllWithLocationInfinite.pathKey()[0]
        )
      )
        return;

      // Debounce tag count updates
      clearTimeout(cancel);
      cancel = setTimeout(() => {
        const data = qc.getQueriesData<
          InfiniteData<
            inferOutput<typeof trpc.productionJob.getAllWithLocationInfinite>
          >
        >(
          trpc.productionJob.getAllWithLocationInfinite.infiniteQueryFilter(
            undefined,
            {
              type: "active",
            }
          )
        );

        const newTagCount = data.reduce((acc, [, item]) => {
          const count = item?.pages[0].totalCount ?? 0;
          return acc + count;
        }, 0);
        if (tagCount !== newTagCount) {
          setTagCount(newTagCount);
        }
      }, 200); // Increased debounce time for better performance
    });

    return () => {
      clearTimeout(cancel);
      u();
    };
  }, [qc, trpc.productionJob.getAllWithLocationInfinite, tagCount, trpc]);

  const downloadMutation = useMutation(
    trpc.rfid.exportTagsToXLSX.mutationOptions()
  );

  // Memoize the download handler to prevent recreating it on each render
  const handleDownloadClick = useCallback(() => {
    downloadMutation.mutate(undefined, {
      onError: (e) => {
        toast.error(
          `Error downloading report: ${e.message || "Unknown error"}`
        );
        console.error("Error downloading XLSX:", e);
      },
      onSuccess: async (data) => {
        try {
          // Modern approach using fetch and Blob
          const base64String = data.data;
          const paddedBase64 = base64String
            .padEnd(
              base64String.length + ((4 - (base64String.length % 4 || 4)) % 4),
              "="
            )
            .replace(/-/g, "+")
            .replace(/_/g, "/");

          // Convert base64 to array buffer using Fetch API
          const response = await fetch(
            `data:application/octet-stream;base64,${paddedBase64}`
          );
          const blob = await response.blob();

          // Create a URL for the blob with correct MIME type
          const blobWithType = new Blob([await blob.arrayBuffer()], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = URL.createObjectURL(blobWithType);

          // Create a temporary anchor element
          const link = document.createElement("a");
          link.href = url;
          link.download = `RFID Tags (${DateTime.now().toFormat("ff")}).xlsx`;

          // Append to the document, click it, and remove it
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the URL object
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Error downloading file:", error);
          toast.error("Failed to download the export file");
        }
      },
    });
  }, [downloadMutation]);

  const { data: cells } = useQuery(
    trpc.cell.getAll.queryOptions(
      {
        byFeatureFlag: CellFeaturesEnum.RFID,
      },
      {
        select: (data) =>
          data.sort((a, b) => a.locationName.localeCompare(b.locationName)),
        refetchOnWindowFocus: true,
        refetchIntervalInBackground: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      }
    )
  );

  const selectedCells = useMemo(() => {
    if (!cells) return [];
    return cells.filter((cell) => locations.includes(cell.cellId));
  }, [cells, locations]);

  useSubscription("onProductionJobUpdate", (payload) => {
    if (!payload) {
      qc.invalidateQueries(
        trpc.productionJob.getAllWithLocationInfinite.infiniteQueryFilter()
      );
      return;
    }
    const data = qc.getQueriesData<
      InfiniteData<
        inferOutput<typeof trpc.productionJob.getAllWithLocationInfinite>
      >
    >(trpc.productionJob.getAllWithLocationInfinite.infiniteQueryFilter());

    const filtered = data.filter((item) =>
      item[1]?.pages.some((page) =>
        page.data.some((job) => payload.ids.includes(job.id))
      )
    );

    if (filtered?.length === 0) return;

    const keys = filtered.map((item) => item[0]);
    for (const key of keys) {
      qc.invalidateQueries({
        queryKey: key,
      });
    }
  });

  useSubscription(
    "onTagLocationUpdate",
    (payload) => {
      if (!payload) {
        qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationInfinite.infiniteQueryFilter()
        );
        return;
      }
      payload.forEach((x) => {
        qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationInfinite.infiniteQueryFilter({
            locationId: x.newLocationId,
          })
        );

        if (x.oldLocationId) {
          qc.invalidateQueries(
            trpc.productionJob.getAllWithLocationInfinite.infiniteQueryFilter({
              locationId: x.oldLocationId,
            })
          );
        }
      });
    },
    [locations]
  );

  // Memoize the devices list to prevent unnecessary re-renders
  const memoizedDevices = useMemo(() => {
    if (!selectedCells?.length) {
      return [
        <Card sx={{ p: 2, bgcolor: "warning.main" }} raised>
          <Typography variant="overline" color="text">
            <strong>WARNING: No equipment currently selected.</strong>
          </Typography>
        </Card>,
      ];
    }

    // Extract stable props to reduce unnecessary rerenders
    const stableSearchOptions = Object.freeze({ ...searchValue });

    const result = selectedCells.map((cell) => (
      <RFIDWidget
        searchOptions={stableSearchOptions}
        sx={{
          width: "350px",
          height: "380px",
        }}
        key={cell.cellId}
        location={cell}
        setSelectedJob={setSelectedJob}
        setShowSelectionModal={setShowSelectionModal}
      />
    ));
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCells,
    // Deep compare searchValue instead of using the reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(searchValue),
    setSelectedJob,
    setShowSelectionModal,
  ]);

  return (
    <Box>
      <PageHeader>RFID Overview</PageHeader>
      <Stack direction="row" gap={2} sx={{ justifyContent: "end", mb: 1 }}>
        <Button
          variant="text"
          sx={{
            py: 0,
            backgroundColor: (theme) => theme.palette.action.hover,
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          }}
          onClick={() => setIsDisplayMode((prev) => !prev)}
        >
          Turn {isDisplayMode ? "Off" : "On"} Display Mode
        </Button>
      </Stack>
      <Collapse in={!isDisplayMode} sx={{ mb: 2 }}>
        <Stack direction="row" gap={2} sx={{ alignItems: "center" }}>
          <Tooltip title="Toggle Search Bar">
            <IconButton
              sx={{
                p: 2,
                borderRadius: "50%",
              }}
              onClick={() => setShowSearchBar((old) => !old)}
            >
              <Search />
            </IconButton>
          </Tooltip>
          <RFIDSelectStationWidget
            selectedLocations={[locations, setLocations]}
            sx={{ flex: "1 1 0" }}
          />
          {hasPermission("RFID::ExportTagLocation") && (
            <Tooltip title="Export to XLSX">
              <IconButton
                sx={{
                  backgroundColor: "background.default",
                  p: 2,
                  borderRadius: "50%",
                }}
                loading={downloadMutation.isPending}
                onClick={handleDownloadClick}
              >
                <SendToMobile />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Collapse in={showSearchBar}>
          <RFIDSearchBar open={showSearchBar} setSearchValue={setSearchValue} />
        </Collapse>
      </Collapse>

      <Stack
        direction="row"
        gap={1}
        sx={{
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {memoizedDevices}
      </Stack>

      <SelectionModal
        open={showSelectionModal}
        onClose={() => setShowSelectionModal(false)}
        onSelect={(option) => {
          if (option === "WorkHistory") return;
          setShowSelectionModal(false);
          setCurrentModal(option);
        }}
      />
      {selectedJob && (
        <>
          <UpdateProductionJobModal
            open={currentModal === "UpdateWorkOrder"}
            onClose={() => {
              setCurrentModal(undefined);
              setSelectedJob(undefined);
            }}
            job={selectedJob}
            cells={cells ?? []}
          />
          <RFIDTagHistoryAndMoveModal
            open={currentModal === "RFIDUtils"}
            onClose={() => setCurrentModal(undefined)}
            id={"0" + selectedJob.id}
          />
        </>
      )}

      <WorkOrderCount totalCount={tagCount} />
    </Box>
  );
}
